import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Building2, Check, FileText, Mail, History, Settings, Info, Printer } from 'lucide-react';
import type { PaymentData } from '../quick-pay/BalanceAndPay';
import { RecentPayments } from './RecentPayments';
interface AccountDashboardProps {
  email: string;
  onContinue: (data: PaymentData) => void;
}
const TOTAL_BALANCE = 854.7;
function formatCurrency(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}
function computePlanSummary(monthly: number) {
  if (!monthly || monthly <= 0) return {
    months: 0,
    label: '—'
  };
  const months = Math.ceil(TOTAL_BALANCE / monthly);
  const endDate = new Date();
  endDate.setMonth(endDate.getMonth() + months - 1);
  const label = `${endDate.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric'
  })}`;
  return {
    months,
    label
  };
}
type Frequency = 'weekly' | 'biweekly' | 'semimonthly' | 'monthly' | 'custom';
function buildSchedule(firstDateISO: string, frequency: Frequency, installmentAmount: number, customDays: number): {
  date: Date;
  amount: number;
}[] {
  if (installmentAmount <= 0) return [];
  const start = new Date(firstDateISO + 'T00:00:00');
  if (isNaN(start.getTime())) return [];
  const out: {
    date: Date;
    amount: number;
  }[] = [];
  let remaining = TOTAL_BALANCE;
  let i = 0;
  const MAX = 120;
  while (remaining > 0 && i < MAX) {
    const d = new Date(start);
    if (frequency === 'weekly') {
      d.setDate(d.getDate() + i * 7);
    } else if (frequency === 'biweekly') {
      d.setDate(d.getDate() + i * 14);
    } else if (frequency === 'semimonthly') {
      // alternate ~15 days
      d.setDate(d.getDate() + i * 15);
    } else if (frequency === 'monthly') {
      d.setMonth(d.getMonth() + i);
    } else {
      d.setDate(d.getDate() + i * Math.max(1, customDays));
    }
    const amount = Math.min(remaining, installmentAmount);
    out.push({
      date: d,
      amount: parseFloat(amount.toFixed(2))
    });
    remaining = parseFloat((remaining - amount).toFixed(2));
    i += 1;
  }
  return out;
}
const sidebarItems = [{
  id: 'billing',
  name: 'Billing',
  icon: <FileText size={16} data-id="element-275" />
}, {
  id: 'statements',
  name: 'Statements & Letters',
  icon: <Mail size={16} data-id="element-276" />
}, {
  id: 'history',
  name: 'Payment History',
  icon: <History size={16} data-id="element-277" />
}, {
  id: 'settings',
  name: 'Account Settings',
  icon: <Settings size={16} data-id="element-278" />
}] as const;
type TabId = (typeof sidebarItems)[number]['id'];
export function AccountDashboard({
  email,
  onContinue
}: AccountDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabId>('billing');
  const [paymentType, setPaymentType] = useState<'full' | 'settlement' | 'plan'>('full');
  const SETTLEMENT_DISCOUNT = 0.3;
  const SETTLEMENT_AMOUNT = parseFloat((TOTAL_BALANCE * (1 - SETTLEMENT_DISCOUNT)).toFixed(2));
  const [monthlyAmount, setMonthlyAmount] = useState<number>(72);
  const [paymentDay, setPaymentDay] = useState<number>(5);
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'semimonthly' | 'monthly' | 'custom'>('monthly');
  const [customDays, setCustomDays] = useState<number>(14);
  const [firstPayDate, setFirstPayDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const sevenDaysOutISO = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();
  const [method, setMethod] = useState<'card' | 'ach' | null>('card');
  // One-time Pay in Full date — defaults to today
  const [payDate, setPayDate] = useState<string>(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const todayISO = (() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  // Card fields
  const [cardNumber, setCardNumber] = useState('•••• •••• •••• 3456');
  const [cardExp, setCardExp] = useState('12/27');
  const [cardCvv, setCardCvv] = useState('•••');
  // ACH fields
  const [routing, setRouting] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const plan = computePlanSummary(monthlyAmount);
  const canSubmit = method !== null && (paymentType === 'full' || paymentType === 'settlement' || monthlyAmount > 0 && paymentDay >= 1) && (method === 'card' ? cardNumber.length >= 4 && cardExp.length > 0 : routing.length >= 4 && bankAccount.length >= 4);
  const handleSubmit = () => {
    if (!canSubmit || !method) return;
    onContinue({
      paymentType,
      fullAmount: TOTAL_BALANCE,
      monthlyAmount,
      paymentDay,
      planMonths: plan.months,
      planEndsLabel: `${plan.months} monthly payments ending ${plan.label}`,
      method,
      cardLast4: cardNumber.replace(/\D/g, '').slice(-4),
      routingLast4: bankAccount.slice(-4),
      payDate: paymentType === 'full' || paymentType === 'settlement' ? payDate : undefined
    });
  };
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35
  }} className="-mx-4 sm:-mx-6 lg:-mx-8" data-id="element-279">
      <div className="px-4 sm:px-6 lg:px-8" data-id="element-280">
        <p className="text-xs text-brand-muted mb-6" data-id="element-281">
          Signed in as{' '}
          <span className="text-brand-dark font-semibold" data-id="element-282">{email}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-0 md:gap-8 px-4 sm:px-6 lg:px-8" data-id="element-283">
        {/* Left Sidebar */}
        <aside className="hidden md:block" data-id="element-284">
          <nav data-id="element-285">
            <ul className="space-y-0.5" data-id="element-286">
              {sidebarItems.map(item => {
              const isActive = activeTab === item.id;
              return <li key={item.id} data-id="element-287">
                    <button type="button" onClick={() => setActiveTab(item.id)} className={`relative w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-r transition-colors ${isActive ? 'text-brand-red bg-brand-red/5 border-l-[3px] border-brand-red pl-2.5 font-bold' : 'text-brand-dark hover:text-brand-navy hover:bg-gray-50 border-l-[3px] border-transparent pl-2.5'}`} data-id="element-288">
                      <span className={isActive ? 'text-brand-red' : 'text-brand-muted'} data-id="element-289">
                        {item.icon}
                      </span>
                      {item.name}
                    </button>
                  </li>;
            })}
            </ul>
          </nav>
        </aside>

        {/* Main column */}
        <div data-id="element-290">
          {/* Section header */}
          <div className="flex items-start justify-between mb-5" data-id="element-291">
            <h1 className="text-2xl md:text-3xl font-bold text-brand-red" data-id="element-292">
              Account
            </h1>
            <div className="flex items-center gap-2 text-brand-muted" data-id="element-293">
              <button aria-label="Print" className="p-1.5 rounded hover:bg-gray-100 transition-colors" data-id="element-294">
                <Printer size={18} data-id="element-295" />
              </button>
              <button aria-label="Help" className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" data-id="element-296">
                <Info size={18} data-id="element-297" />
              </button>
            </div>
          </div>

          {activeTab === 'billing' && <>
              {/* Summary card */}
              <div className="border border-gray-200 rounded-md p-6 mb-5" data-id="element-298">
                <div className="flex flex-col md:flex-row gap-8 md:items-start md:justify-between" data-id="element-299">
                  <div className="min-w-0" data-id="element-300">
                    <p className="text-sm text-brand-dark" data-id="element-301">
                      Original Creditor:{' '}
                      <span className="font-semibold" data-id="element-302">
                        Education Corp of America (G)
                      </span>
                    </p>
                    <p className="text-sm text-brand-dark mb-4" data-id="element-303">
                      Account holder:{' '}
                      <span className="font-semibold" data-id="element-304">Jane Berpan</span>
                    </p>
                    <p className="text-sm font-semibold text-brand-dark mb-1" data-id="element-305">
                      Outstanding Balance
                    </p>
                    <p className="text-3xl font-bold text-brand-dark" data-id="element-306">
                      {formatCurrency(TOTAL_BALANCE)}
                    </p>
                    <p className="text-xs text-brand-muted mt-1 flex items-center gap-1" data-id="element-307">
                      Total balance: {formatCurrency(TOTAL_BALANCE)}
                      <Info size={12} data-id="element-308" />
                    </p>
                  </div>

                  <div className="flex flex-col items-stretch md:items-end gap-5 md:min-w-[220px]" data-id="element-309">
                    <div className="flex gap-3 md:flex-col md:items-end" data-id="element-310">
                      <button type="button" onClick={() => {
                    setPaymentType('full');
                    document.getElementById('payment-section')?.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }} aria-pressed={paymentType === 'full'} className={`px-5 py-2 text-sm font-bold uppercase tracking-wider rounded transition-colors bg-brand-navy text-white hover:bg-blue-900 border-2 border-brand-navy ${paymentType === 'full' ? 'ring-2 ring-brand-navy/30 ring-offset-1' : ''}`} data-id="element-311">
                        Pay in Full
                      </button>
                      <button type="button" onClick={() => {
                    setPaymentType('settlement');
                    document.getElementById('payment-section')?.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }} aria-pressed={paymentType === 'settlement' || paymentType === 'plan'} className={`px-5 py-2 text-sm font-bold uppercase tracking-wider rounded transition-colors whitespace-nowrap bg-white text-brand-navy border-2 border-brand-navy hover:bg-brand-navy/5 ${paymentType === 'settlement' || paymentType === 'plan' ? 'ring-2 ring-brand-navy/30 ring-offset-1' : ''}`} data-id="element-312">
                        View Offer
                      </button>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2 text-sm pt-1" data-id="element-313">
                      <a href="#" className="flex items-center gap-1.5 text-brand-navy hover:text-brand-red transition-colors font-medium" data-id="element-314">
                        <FileText size={14} data-id="element-315" />
                        Billing Details
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-brand-muted mb-8 px-1" data-id="element-316">
                If you would like to receive paper statements, you may{' '}
                <a href="#" className="text-brand-red hover:underline font-medium" data-id="element-317">
                  cancel paperless billing
                </a>
                .
              </p>

              {/* Payment Setup */}
              <div id="payment-section" className="mb-10 mt-2" data-id="element-318">
                <h2 className="text-xl font-bold text-brand-red mb-5" data-id="element-319">
                  {paymentType === 'full' ? 'Pay Your Balance' : paymentType === 'settlement' ? 'Settlement Offer' : 'Set Up Your Payment Plan'}
                </h2>

                {/* Offer sub-toggle */}
                {(paymentType === 'settlement' || paymentType === 'plan') && <div className="inline-flex p-1 bg-gray-100 rounded-lg mb-5 border border-gray-300" data-id="element-320">
                    <button type="button" onClick={() => setPaymentType('settlement')} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${paymentType === 'settlement' ? 'bg-white text-brand-navy shadow-sm' : 'text-brand-muted hover:text-brand-navy'}`} data-id="element-321">
                      Payment Settlement
                    </button>
                    <button type="button" onClick={() => setPaymentType('plan')} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${paymentType === 'plan' ? 'bg-white text-brand-navy shadow-sm' : 'text-brand-muted hover:text-brand-navy'}`} data-id="element-322">
                      Set Up Payment Plan
                    </button>
                  </div>}

                <p className="text-xs text-brand-muted mb-5" data-id="element-323">
                  * Field required
                </p>

                <p className="text-sm text-brand-dark mb-6" data-id="element-324">
                  Outstanding Balance:{' '}
                  <span className="font-semibold" data-id="element-325">
                    {formatCurrency(TOTAL_BALANCE)}
                  </span>
                </p>

                <AnimatePresence mode="wait" data-id="element-326">
                  {paymentType === 'settlement' && <motion.div key="settlement-fields" initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-327">
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 mb-5" data-id="element-328">
                        <div className="flex items-start justify-between gap-4 mb-3" data-id="element-329">
                          <div data-id="element-330">
                            <p className="text-xs font-bold text-green-800 uppercase tracking-wider" data-id="element-331">
                              Pre-configured Settlement
                            </p>
                            <p className="text-3xl font-bold text-brand-navy mt-1" data-id="element-332">
                              {formatCurrency(SETTLEMENT_AMOUNT)}
                            </p>
                            <p className="text-xs text-brand-muted mt-1" data-id="element-333">
                              One-time payment to fully resolve your balance
                            </p>
                          </div>
                          <span className="px-2.5 py-1 rounded-full bg-white text-green-700 text-xs font-bold border border-green-200 shrink-0" data-id="element-334">
                            {Math.round(SETTLEMENT_DISCOUNT * 100)}% off
                          </span>
                        </div>
                        <div className="text-xs text-brand-muted flex items-center justify-between border-t border-green-200 pt-3 mt-3" data-id="element-335">
                          <span data-id="element-336">You save</span>
                          <span className="font-bold text-green-700" data-id="element-337">
                            {formatCurrency(TOTAL_BALANCE - SETTLEMENT_AMOUNT)}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-338">
                        <div data-id="element-339">
                          <label htmlFor="settle-amount" className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-340">
                            Settlement Amount*
                          </label>
                          <div className="relative" data-id="element-341">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm" data-id="element-342">
                              $
                            </span>
                            <input id="settle-amount" type="text" readOnly value={SETTLEMENT_AMOUNT.toFixed(2)} className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded text-brand-dark bg-gray-50" data-id="element-343" />
                          </div>
                        </div>
                        <div data-id="element-344">
                          <label htmlFor="settle-date" className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-345">
                            Pay Date*
                          </label>
                          <input id="settle-date" type="date" min={todayISO} value={payDate} onChange={e => setPayDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-brand-dark focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy transition-colors" data-id="element-346" />
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <AnimatePresence mode="wait" data-id="element-347">
                  {paymentType === 'full' && <motion.div key="full-fields" initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-348">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-349">
                        <div data-id="element-350">
                          <label htmlFor="full-amount" className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-351">
                            Amount to Pay*
                          </label>
                          <div className="relative" data-id="element-352">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm" data-id="element-353">
                              $
                            </span>
                            <input id="full-amount" type="text" readOnly value={TOTAL_BALANCE.toFixed(2)} className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded text-brand-dark bg-gray-50" data-id="element-354" />
                          </div>
                        </div>
                        <div data-id="element-355">
                          <label htmlFor="pay-date" className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-356">
                            Pay Date*
                          </label>
                          <input id="pay-date" type="date" min={todayISO} value={payDate} onChange={e => setPayDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-brand-dark focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy transition-colors" data-id="element-357" />
                        </div>
                      </div>
                    </motion.div>}

                  {paymentType === 'plan' && <motion.div key="plan-fields" initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-358">
                      {/* Installment amount */}
                      <div className="mb-4" data-id="element-359">
                        <label htmlFor="installment" className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-360">
                          Installment Amount*
                        </label>
                        <div className="relative max-w-xs" data-id="element-361">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm" data-id="element-362">
                            $
                          </span>
                          <input id="installment" type="number" min={25} step={1} value={monthlyAmount} onChange={e => setMonthlyAmount(Number(e.target.value))} className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded text-brand-dark focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy transition-colors" data-id="element-363" />
                        </div>
                      </div>

                      {/* First Payment Date */}
                      <div className="mb-4" data-id="element-364">
                        <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-365">
                          First Payment Date*
                        </label>
                        <p className="text-xs text-brand-muted mb-2" data-id="element-366">
                          Pick any day in the next 7 days
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-367">
                          {[{
                      label: 'Tomorrow',
                      days: 1
                    }, {
                      label: 'In 3 days',
                      days: 3
                    }, {
                      label: 'In 7 days',
                      days: 7
                    }].map(opt => {
                      const d = new Date();
                      d.setDate(d.getDate() + opt.days);
                      const iso = d.toISOString().slice(0, 10);
                      const active = firstPayDate === iso;
                      return <button key={opt.label} type="button" onClick={() => setFirstPayDate(iso)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${active ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-300 hover:border-brand-navy'}`} data-id="element-368">
                                {opt.label}
                              </button>;
                    })}
                        </div>
                        <input type="date" min={todayISO} max={sevenDaysOutISO} value={firstPayDate} onChange={e => setFirstPayDate(e.target.value)} className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded text-brand-dark focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy transition-colors" data-id="element-369" />
                      </div>

                      {/* Frequency */}
                      <div className="mb-4" data-id="element-370">
                        <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1.5" data-id="element-371">
                          Recurrence*
                        </label>
                        <div className="flex flex-wrap gap-2" data-id="element-372">
                          {([{
                      id: 'weekly',
                      label: 'Weekly'
                    }, {
                      id: 'biweekly',
                      label: 'Biweekly'
                    }, {
                      id: 'semimonthly',
                      label: 'Twice a month'
                    }, {
                      id: 'monthly',
                      label: 'Monthly'
                    }, {
                      id: 'custom',
                      label: 'Custom'
                    }] as const).map(opt => {
                      const active = frequency === opt.id;
                      return <button key={opt.id} type="button" onClick={() => setFrequency(opt.id)} className={`px-3.5 py-2 rounded-full text-xs font-bold transition-colors border ${active ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-300 hover:border-brand-navy'}`} data-id="element-373">
                                {opt.label}
                              </button>;
                    })}
                        </div>
                        {frequency === 'custom' && <div className="mt-3 flex items-center gap-2 text-sm" data-id="element-374">
                            <span className="text-brand-muted" data-id="element-375">Every</span>
                            <input type="number" min={1} max={60} value={customDays} onChange={e => setCustomDays(Math.max(1, Number(e.target.value)))} className="w-20 px-2 py-1.5 border border-gray-300 rounded text-brand-dark text-sm focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy" data-id="element-376" />
                            <span className="text-brand-muted" data-id="element-377">days</span>
                          </div>}
                      </div>

                      {(() => {
                  const schedule = buildSchedule(firstPayDate, frequency, monthlyAmount, customDays);
                  const last = schedule[schedule.length - 1];
                  const freqWord = frequency === 'weekly' ? 'every week' : frequency === 'biweekly' ? 'every two weeks' : frequency === 'semimonthly' ? 'twice a month' : frequency === 'monthly' ? 'each month' : `every ${customDays} days`;
                  return <>
                            <p className="italic text-sm text-brand-dark mb-4 leading-relaxed" data-id="element-378">
                              We will charge your payment method{' '}
                              <strong data-id="element-379">{formatCurrency(monthlyAmount)}</strong>{' '}
                              {freqWord}. It will take{' '}
                              <strong data-id="element-380">{schedule.length} payments</strong> to pay
                              off your outstanding balance of{' '}
                              <strong data-id="element-381">{formatCurrency(TOTAL_BALANCE)}</strong>.{' '}
                              {last && <>
                                  Your last payment will be on{' '}
                                  <strong data-id="element-382">
                                    {last.date.toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                                  </strong>
                                  .
                                </>}
                            </p>

                            {schedule.length > 0 && <div className="border border-gray-200 rounded-lg overflow-hidden mb-6" data-id="element-383">
                                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between" data-id="element-384">
                                  <p className="text-xs font-bold text-brand-navy uppercase tracking-wider" data-id="element-385">
                                    Payment Schedule
                                  </p>
                                  <p className="text-xs text-brand-muted" data-id="element-386">
                                    {schedule.length}{' '}
                                    {schedule.length === 1 ? 'payment' : 'payments'}
                                  </p>
                                </div>
                                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100" data-id="element-387">
                                  {schedule.map((p, i) => {
                          const dateLabel = p.date.toLocaleString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                          return <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-388">
                                        <div className="flex items-center gap-3" data-id="element-389">
                                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-brand-navy text-white' : 'bg-gray-100 text-brand-muted'}`} data-id="element-390">
                                            {i + 1}
                                          </span>
                                          <p className="font-semibold text-brand-dark" data-id="element-391">
                                            {dateLabel}
                                          </p>
                                        </div>
                                        <span className="font-bold text-brand-navy" data-id="element-392">
                                          {formatCurrency(p.amount)}
                                        </span>
                                      </div>;
                        })}
                                </div>
                              </div>}
                          </>;
                })()}
                    </motion.div>}
                </AnimatePresence>

                {/* Payment Method */}
                <div className="border border-gray-200 rounded-md p-5 md:p-6 mt-2" data-id="element-393">
                  <p className="text-sm font-semibold text-brand-dark mb-4" data-id="element-394">
                    Payment Method
                  </p>
                  <div className="space-y-2" data-id="element-395">
                    <MethodOption selected={method === 'card'} onSelect={() => setMethod('card')} icon={<div className="w-9 h-6 bg-gradient-to-br from-brand-navy to-blue-700 rounded-sm flex items-center justify-center" data-id="element-397">
                          <CreditCard size={14} className="text-white" data-id="element-398" />
                        </div>} label="Credit / Debit Card" data-id="element-396">
                      <AnimatePresence data-id="element-399">
                        {method === 'card' && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-400">
                            <div className="pt-3 mt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-401">
                              <div className="sm:col-span-2" data-id="element-402">
                                <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1" data-id="element-403">
                                  Card Number
                                </label>
                                <input type="text" value={cardNumber} onChange={e => setCardNumber(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy" data-id="element-404" />
                              </div>
                              <div className="grid grid-cols-2 gap-2" data-id="element-405">
                                <div data-id="element-406">
                                  <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1" data-id="element-407">
                                    Exp
                                  </label>
                                  <input type="text" value={cardExp} onChange={e => setCardExp(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy" data-id="element-408" />
                                </div>
                                <div data-id="element-409">
                                  <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1" data-id="element-410">
                                    CVV
                                  </label>
                                  <input type="text" value={cardCvv} onChange={e => setCardCvv(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy" data-id="element-411" />
                                </div>
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </MethodOption>

                    <MethodOption selected={method === 'ach'} onSelect={() => setMethod('ach')} icon={<div className="w-9 h-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-sm flex items-center justify-center" data-id="element-413">
                          <Building2 size={14} className="text-white" data-id="element-414" />
                        </div>} label="Bank Account (ACH)" data-id="element-412">
                      <AnimatePresence data-id="element-415">
                        {method === 'ach' && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-416">
                            <div className="pt-3 mt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-417">
                              <div data-id="element-418">
                                <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1" data-id="element-419">
                                  Routing Number
                                </label>
                                <input type="text" value={routing} onChange={e => setRouting(e.target.value)} placeholder="9 digits" className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy" data-id="element-420" />
                              </div>
                              <div data-id="element-421">
                                <label className="block text-[11px] font-bold tracking-wider text-brand-muted uppercase mb-1" data-id="element-422">
                                  Account Number
                                </label>
                                <input type="text" value={bankAccount} onChange={e => setBankAccount(e.target.value)} placeholder="Account number" className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy" data-id="element-423" />
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </MethodOption>
                  </div>

                  <div className="mt-7 flex justify-end" data-id="element-424">
                    <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="bg-brand-red hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-8 py-2.5 rounded font-bold uppercase tracking-wider text-sm transition-colors shadow-sm" data-id="element-425">
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </>}

          {activeTab === 'history' && <div className="mt-2" data-id="element-426">
              <RecentPayments data-id="element-427" />
            </div>}

          {activeTab === 'statements' && <div className="border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-brand-muted" data-id="element-428">
              No statements or letters available yet.
            </div>}

          {activeTab === 'settings' && <div className="border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-brand-muted" data-id="element-429">
              Account settings coming soon.
            </div>}
        </div>
      </div>
    </motion.div>;
}
interface MethodOptionProps {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  label: string;
  children?: React.ReactNode;
}
function MethodOption({
  selected,
  onSelect,
  icon,
  label,
  children
}: MethodOptionProps) {
  return <div className={`border rounded transition-colors ${selected ? 'border-brand-navy bg-brand-navy/[0.02]' : 'border-gray-200'}`} data-id="element-430">
      <button type="button" onClick={onSelect} className="w-full flex items-center gap-3 px-3 py-2.5 text-left" aria-pressed={selected} data-id="element-431">
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? 'border-brand-navy bg-brand-navy' : 'border-gray-300'}`} data-id="element-432">
          {selected && <Check size={10} className="text-white" data-id="element-433" />}
        </div>
        {icon}
        <span className="text-sm font-medium text-brand-dark" data-id="element-434">{label}</span>
      </button>
      {children && <div className="px-3 pb-3" data-id="element-435">{children}</div>}
    </div>;
}